import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import { useTranslation } from "react-i18next";
import commonStore from "../../Store/marco/commonStore";
import {
  buildCustomEFSharingPayload,
  getNodeId,
  getNodeLabel,
  normalizeCustomEFSharing,
} from "../services/customEFSharing";
import styles from "../styles/customEFSharingSettings.module.css";

const getParentId = (node) =>
  String(node?.parentId ?? node?.parent_id ?? "");

const getNodeStatus = (node) =>
  String(node?.nodeStatus ?? "").toLowerCase();

const isCategoryNode = (node) =>
  String(node?.orgType ?? "").toUpperCase() === "CATEGORY" ||
  getNodeStatus(node) === "readonly";

const isCompanyNode = (node) => {
  const orgType = String(node?.orgType ?? "").toUpperCase();
  return !isCategoryNode(node) && (!orgType || orgType === "COMPANY");
};

const isCoveredCompany = (node) =>
  isCompanyNode(node) && getNodeStatus(node) === "editable";

const isSelectableCompany = (node) => {
  if (!isCompanyNode(node) || isCoveredCompany(node)) return false;

  const status = getNodeStatus(node);
  return !status || status === "hidden";
};

const buildTree = (items) => {
  const nodes = new Map();
  const roots = [];

  (Array.isArray(items) ? items : []).forEach((item) => {
    const id = getNodeId(item);
    if (!id) return;
    nodes.set(id, { ...item, id, children: [] });
  });

  nodes.forEach((node) => {
    const parentId = getParentId(node);
    if (parentId && nodes.has(parentId)) {
      nodes.get(parentId).children.push(node);
    } else {
      roots.push(node);
    }
  });

  return { roots, nodes };
};

const getSelectableCompanyIds = (node) => {
  const ids = isSelectableCompany(node) ? [node.id] : [];

  node.children.forEach((child) => {
    ids.push(...getSelectableCompanyIds(child));
  });

  return ids;
};

const getCompanyNodes = (node) => {
  const companies = isCompanyNode(node) ? [node] : [];

  node.children.forEach((child) => {
    companies.push(...getCompanyNodes(child));
  });

  return companies;
};

const getDefaultExpandedIds = (roots) => {
  const expanded = new Set();

  roots.forEach((root) => {
    if (!root.children.length) return;
    expanded.add(root.id);
    root.children.forEach((child) => {
      if (child.children.length) expanded.add(child.id);
    });
  });

  return expanded;
};

const SharingTreeNode = ({
  node,
  selectedIds,
  expandedIds,
  onToggleExpanded,
  onToggleSelected,
  t,
}) => {
  const selectableIds = getSelectableCompanyIds(node);
  const isCategory = isCategoryNode(node);
  const isCovered = isCoveredCompany(node);
  const companyNodes = isCategory ? getCompanyNodes(node) : [];
  const isUnavailableCompany =
    isCompanyNode(node) && !isCovered && !isSelectableCompany(node);
  const isSelected = isCategory
    ? companyNodes.length > 0 &&
      companyNodes.every(
        (company) =>
          isCoveredCompany(company) || selectedIds.has(company.id),
      )
    : isCovered || selectedIds.has(node.id);
  const selectionDisabled =
    isCovered || isUnavailableCompany || selectableIds.length === 0;
  const isExpanded = expandedIds.has(node.id);
  const hasChildren = node.children.length > 0;

  return (
    <div className={styles.treeNode}>
      <div
        className={`${styles.treeRow} ${
          isCategory ? styles.categoryRow : styles.companyRow
        } ${selectionDisabled ? styles.disabledRow : ""}`}
      >
        <button
          type="button"
          className={styles.expandButton}
          onClick={() => onToggleExpanded(node.id)}
          aria-label={t(hasChildren ? "Toggle organization branch" : "Leaf node")}
          disabled={!hasChildren}
        >
          {hasChildren ? (
            isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />
          ) : null}
        </button>
        <Checkbox
          className={styles.treeCheckbox}
          size="small"
          checked={isSelected}
          indeterminate={false}
          disabled={selectionDisabled}
          onChange={() => onToggleSelected(selectableIds, isSelected)}
        />
        <span
          className={`${styles.nodeIcon} ${
            isCategory ? styles.categoryIcon : styles.companyIcon
          }`}
          aria-hidden="true"
        >
          {isCategory ? <AccountTreeOutlinedIcon /> : <BusinessOutlinedIcon />}
        </span>
        <div
          className={
            isCovered
              ? styles.coveredLabel
              : isUnavailableCompany
                ? styles.unavailableLabel
                : isCategory
                  ? styles.categoryLabel
                  : styles.nodeLabel
          }
        >
          <span>{getNodeLabel(node) || t("Unnamed organization")}</span>
          {isCovered && (
            <small>{t("Already covered by internal sharing")}</small>
          )}
        </div>
      </div>
      {hasChildren && isExpanded && (
        <div className={styles.treeChildren}>
          {node.children.map((child) => (
            <SharingTreeNode
              key={child.id}
              node={child}
              selectedIds={selectedIds}
              expandedIds={expandedIds}
              onToggleExpanded={onToggleExpanded}
              onToggleSelected={onToggleSelected}
              t={t}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const CustomEFSharingSettings = ({ value, onChange, showValidationError }) => {
  const { t } = useTranslation("ActivityMain");
  const sharing = normalizeCustomEFSharing(value);
  const [treeOpen, setTreeOpen] = useState(false);
  const [temporaryIds, setTemporaryIds] = useState(new Set());
  const [expandedIds, setExpandedIds] = useState(new Set());
  const companyList = commonStore.companyList;
  const orgList = commonStore.orgList;
  const treeSource =
    commonStore.scopeType === "AUTH" ? orgList : companyList;
  const { roots } = useMemo(() => buildTree(treeSource), [treeSource]);
  const selectableCompanyIds = useMemo(
    () =>
      new Set(
        (Array.isArray(treeSource) ? treeSource : [])
          .filter(isSelectableCompany)
          .map(getNodeId)
          .filter(Boolean),
      ),
    [treeSource],
  );
  const selectedCompanyIds = sharing.sharedEntityIds.filter((id) =>
    selectableCompanyIds.has(String(id)),
  );
  const selectedCompanyCount = selectedCompanyIds.length;

  useEffect(() => {
    if (
      value?.shareWithinScope === undefined &&
      value?.sharing?.shareWithinScope === undefined
    ) {
      onChange({
        shareWithinScope: sharing.shareWithinScope,
        shareOutsideScope: sharing.shareOutsideScope,
        sharedEntityIds: sharing.sharedEntityIds,
      });
    }
  }, []); // Defaults are applied once when a new form opens.

  useEffect(() => {
    if (showValidationError && sharing.shareOutsideScope && !selectedCompanyCount) {
      setTemporaryIds(new Set());
      setExpandedIds(getDefaultExpandedIds(roots));
      setTreeOpen(true);
    }
  }, [
    showValidationError,
    sharing.shareOutsideScope,
    selectedCompanyCount,
    roots,
  ]);

  const updateSharing = (next) => {
    const nextSharing = { ...sharing, ...next };
    onChange({
      ...nextSharing,
      ...buildCustomEFSharingPayload(nextSharing),
    });
  };

  const handleWithinScopeChange = (enabled) => {
    if (sharing.shareWithinScope === enabled) return;

    updateSharing(
      enabled
        ? { shareWithinScope: true, shareOutsideScope: false, sharedEntityIds: [] }
        : { shareWithinScope: false, shareOutsideScope: false, sharedEntityIds: [] },
    );
  };

  const openTree = () => {
    setTemporaryIds(
      new Set(selectedCompanyIds.map(String)),
    );
    setExpandedIds(getDefaultExpandedIds(roots));
    setTreeOpen(true);
  };

  const handleOutsideScopeChange = (enabled) => {
    if (sharing.shareOutsideScope === enabled) return;

    updateSharing({ shareOutsideScope: enabled });
    if (enabled) openTree();
  };

  const toggleSelected = (selectableIds, isSelected) => {
    setTemporaryIds((current) => {
      const next = new Set(current);
      if (isSelected) {
        selectableIds.forEach((id) => next.delete(id));
      } else {
        selectableIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  return (
    <section className={styles.container}>
      <div className={styles.heading}>
        <span className={styles.badge}>{t("Sharing")}</span>
        <strong>{t("Sharing Settings")}</strong>
      </div>

      <div className={styles.question}>
        <div>
          <strong>{t("Share with reporting entities within your account?")}</strong>
          <p>{t("Companies within your organization scope can select this emission factor under the same category.")}</p>
        </div>
        <div className={styles.segmentedControl}>
          {[true, false].map((enabled) => (
            <button
              key={String(enabled)}
              type="button"
              className={sharing.shareWithinScope === enabled ? styles.activeOption : ""}
              onClick={() => handleWithinScopeChange(enabled)}
            >
              {t(enabled ? "Yes" : "No")}
            </button>
          ))}
        </div>
      </div>

      <div className={`${styles.question} ${!sharing.shareWithinScope ? styles.disabledQuestion : ""}`}>
        <div>
          <strong>{t("Share with reporting entities outside your account?")}</strong>
          <p>{t("Choose individual companies outside your organization scope.")}</p>
        </div>
        <div className={styles.segmentedControl}>
          {[true, false].map((enabled) => (
            <button
              key={String(enabled)}
              type="button"
              disabled={!sharing.shareWithinScope}
              className={sharing.shareOutsideScope === enabled ? styles.activeOption : ""}
              onClick={() => handleOutsideScopeChange(enabled)}
            >
              {t(enabled ? "Yes" : "No")}
            </button>
          ))}
        </div>
        {sharing.shareOutsideScope && (
          <div className={styles.selectionSummary}>
            <span>
              {t("Selected companies")}: {selectedCompanyCount}
            </span>
            <button type="button" onClick={openTree}>{t("Edit selection")}</button>
          </div>
        )}
        {showValidationError && sharing.shareOutsideScope && !selectedCompanyCount && (
          <div className={styles.validationError}>
            {t("Select at least one company outside your organization scope.")}
          </div>
        )}
      </div>

      <Dialog
        open={treeOpen}
        onClose={() => setTreeOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 11000 }}
        PaperProps={{ className: styles.dialogPaper }}
      >
        <DialogTitle className={styles.dialogTitle}>
          {t("Choose companies to share with")}
          <IconButton onClick={() => setTreeOpen(false)} aria-label={t("Close")}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className={styles.dialogContent}>
          {!Array.isArray(treeSource) ? (
            <div className={styles.treeState}>{t("Unable to load organization tree.")}</div>
          ) : roots.length === 0 ? (
            <div className={styles.treeState}>{t("No companies available.")}</div>
          ) : (
            <div className={styles.treeRoot}>
              {roots.map((node) => (
                <SharingTreeNode
                  key={node.id}
                  node={node}
                  selectedIds={temporaryIds}
                  expandedIds={expandedIds}
                  onToggleExpanded={(id) =>
                    setExpandedIds((current) => {
                      const next = new Set(current);
                      next.has(id) ? next.delete(id) : next.add(id);
                      return next;
                    })
                  }
                  onToggleSelected={toggleSelected}
                  t={t}
                />
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setTreeOpen(false)}>{t("Cancel")}</Button>
          <Button
            variant="contained"
            disabled={roots.length === 0}
            onClick={() => {
              updateSharing({
                sharedEntityIds: Array.from(temporaryIds).filter((id) =>
                  selectableCompanyIds.has(id),
                ),
              });
              setTreeOpen(false);
            }}
          >
            {t("Confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  );
};

export default CustomEFSharingSettings;
